<template>
	<el-dialog v-dialogDrag :title="title" :visible.sync="isShow" :width="dialogWidth" append-to-body
		:close-on-click-modal="false" :before-close="close">
		<div class="group-member-selector">
			<div class="left-box">
				<div class="selector-search-row">
					<el-input placeholder="搜索" v-model="searchText">
						<i class="el-icon-search el-input__icon" slot="suffix"> </i>
					</el-input>
					<el-button class="select-all-btn" type="text" size="mini" :disabled="selectableMembers.length === 0"
						@click="onToggleAll">{{ isAllSelectableChecked ? '取消全选' : '全选' }}</el-button>
				</div>
				<virtual-scroller class="scroll-box" :items="showMembers">
					<template v-slot="{ item }">
						<group-member-item :group="group" :groupMembers="showMembers" :member="item" :menu="false"
							@click.native="onClickMember(item)">
							<el-checkbox :disabled="item.locked" v-model="item.checked" @change="onChange(item)"
								@click.native.stop=""></el-checkbox>
						</group-member-item>
					</template>
				</virtual-scroller>
			</div>
			<div class="arrow el-icon-d-arrow-right"></div>
			<div class="right-box">
				<div class="select-tip"> 已勾选{{ checkedMembers.length }}位成员</div>
				<el-scrollbar class="scroll-box">
					<div class="member-items">
						<div v-for="m in checkedMembers" :key="m.userId">
							<group-member class="member-item" :member="m"></group-member>
						</div>
					</div>
				</el-scrollbar>
			</div>
		</div>
		<span slot="footer" class="dialog-footer">
			<el-button @click="close()">取 消</el-button>
			<el-button type="primary" @click="ok()">确 定</el-button>
		</span>
	</el-dialog>
</template>

<script>
import VirtualScroller from '../common/VirtualScroller.vue';
import GroupMemberItem from './GroupMemberItem.vue';
import GroupMember from './GroupMember.vue';
import {
	applyDialogLeft,
	findVisibleDialogAnchor,
	getAnchoredDialogLayout
} from '../../utils/anchoredDialog';

export default {
	name: "addGroupMember",
	components: {
		GroupMemberItem,
		GroupMember,
		VirtualScroller
	},
	data() {
		return {
			isShow: false,
			searchText: "",
			maxSize: -1,
			members: [],
			dialogWidth: "700px"
		}
	},
	props: {
		group: {
			type: Object
		},
		title: {
			type: String,
			default: "选择成员"
		},
		dialogAnchorSelector: {
			type: String,
			default: ""
		}
	},
	methods: {
		open(maxSize, checkedIds, lockedIds, hideIds) {
			this.maxSize = Math.min(maxSize || 8, 8);
			this.isShow = true;
			this.updateDialogLayout();
			this.loadGroupMembers(checkedIds, lockedIds, hideIds);
			window.addEventListener('resize', this.updateDialogLayout);
		},
		updateDialogLayout() {
			this.$nextTick(() => {
				const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
				const anchor = findVisibleDialogAnchor(this.dialogAnchorSelector);
				const anchorRect = anchor
					? anchor.getBoundingClientRect()
					: { left: 0, width: viewportWidth };
				const layout = getAnchoredDialogLayout(anchorRect, 700, viewportWidth);
				this.dialogWidth = `${layout.width}px`;
				this.$nextTick(() => applyDialogLeft(this.$el, layout.left));
			});
		},
		loadGroupMembers(checkedIds, lockedIds, hideIds) {
			const checkedIdSet = new Set((checkedIds || []).map(id => String(id)));
			const lockedIdSet = new Set((lockedIds || []).map(id => String(id)));
			const hiddenIdSet = new Set((hideIds || []).map(id => String(id)));
			this.$http({
				url: `/group/members/${this.group.id}`,
				method: 'get'
			}).then((members) => {
				members.forEach((m) => {
					const userId = String(m.userId);
					m.hide = hiddenIdSet.has(userId);
					m.locked = lockedIdSet.has(userId);
					// 隐藏成员只是排除项，绝不能被带入右侧已选结果。
					m.checked = !m.hide && checkedIdSet.has(userId);
				});
				this.members = members;
			});
		},
		onClickMember(m) {
			if (!m.locked) {
				m.checked = !m.checked;
			}
			if (this.maxSize > 0 && this.checkedMembers.length > this.maxSize) {
				this.$message.error(`最多选择${this.maxSize}位成员`)
				m.checked = false;
			}
		},
		onChange(m) {
			if (this.maxSize > 0 && this.checkedMembers.length > this.maxSize) {
				this.$message.error(`最多选择${this.maxSize}位成员`)
				m.checked = false;
			}
		},
		onToggleAll() {
			if (this.isAllSelectableChecked) {
				this.selectableMembers.forEach((m) => {
					m.checked = false;
				});
				return;
			}

			let selectedCount = this.checkedMembers.length;
			let reachedLimit = false;
			this.selectableMembers.forEach((m) => {
				if (m.checked) {
					return;
				}
				if (this.maxSize > 0 && selectedCount >= this.maxSize) {
					reachedLimit = true;
					return;
				}
				m.checked = true;
				selectedCount += 1;
			});

			if (reachedLimit) {
				this.$message.warning(`最多选择${this.maxSize}位成员`);
			}
		},
		ok() {
			this.$emit("complete", this.checkedMembers);
			this.close();
		},
		close() {
			this.isShow = false;
			window.removeEventListener('resize', this.updateDialogLayout);
		}
	},
	computed: {
		checkedMembers() {
			return this.members.filter((m) => m.checked && !m.hide && !m.quit);
		},
		showMembers() {
			return this.members.filter((m) => !m.hide && !m.quit && m.showNickName.includes(this.searchText))
		},
		selectableMembers() {
			return this.members.filter((m) => !m.hide && !m.quit && !m.locked);
		},
		isAllSelectableChecked() {
			const checkedSelectableCount = this.selectableMembers.filter(m => m.checked).length;
			if (checkedSelectableCount === 0) {
				return false;
			}
			return checkedSelectableCount === this.selectableMembers.length ||
				(this.maxSize > 0 && this.checkedMembers.length >= this.maxSize);
		}
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.updateDialogLayout);
	}
}
</script>

<style lang="scss" scoped>
.group-member-selector {
	display: flex;
	padding: 20px 24px 0;

	.scroll-box {
		height: 400px;
	}

	.left-box {
		width: 48%;
		overflow: hidden;
		border: var(--im-border);

		.selector-search-row {
			display: flex;
			align-items: center;
			padding-right: 10px;
			border-bottom: var(--im-border);

			.el-input {
				flex: 1;
			}

			.el-input__inner {
				border: none;
			}

			.select-all-btn {
				flex: 0 0 auto;
				padding: 7px 4px;
			}
		}
	}

	.arrow {
		display: flex;
		align-items: center;
		font-size: 20px;
		padding: 10px;
		font-weight: 600;
		color: var(--im-color-primary);
	}

	.right-box {
		width: 48%;
		border: var(--im-border);

		.select-tip {
			text-align: left;
			height: 40px;
			line-height: 40px;
			text-indent: 5px;
			color: var(--im-text-color-light)
		}

		.member-items {
			padding: 10px;
			display: flex;
			flex-direction: row;
			flex-wrap: wrap;

			.member-item {
				padding: 2px;
			}
		}
	}
}

.dialog-footer {
	.el-button--primary {
		background-color: #d4def5;
		border-color: #d4def5;
		color: #111827;
		box-shadow: none;
	}

	.el-button--primary:hover,
	.el-button--primary:focus {
		background-color: #c3cfef;
		border-color: #c3cfef;
		color: #111827;
	}
}
</style>
